import { useState } from 'react'
import toast from 'react-hot-toast'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import { AccessDenied, DateRangeBar, downloadReportCsv, firstOfMonth, today } from './shared'
import TaxReportTab from './TaxReportTab'
import FinancialOverviewTab from './FinancialOverviewTab'

type Tab = 'tax' | 'financial'

const AccountingReportsPage = () => {
  const { hasPermission } = usePermissions()
  const [activeTab, setActiveTab] = useState<Tab>('tax')
  const [startDate, setStartDate] = useState(firstOfMonth())
  const [endDate, setEndDate] = useState(today())
  const [downloading, setDownloading] = useState(false)

  const canView = hasPermission(PERMISSIONS.ORDER_FINANCIAL_VIEW)
  // orderStats needs order.list.all for arbitrary history (lower tiers get silently
  // floored to the last 7 days server-side), so the tab is hidden without it.
  const canFinancialTab = hasPermission(PERMISSIONS.ORDER_LIST_ALL)

  const handleDownloadCSV = async () => {
    setDownloading(true)
    try {
      await downloadReportCsv(
        `/api/reports/tax?start_date=${startDate}&end_date=${endDate}`,
        `tax-report-${startDate}-${endDate}.csv`
      )
    } catch (err) {
      toast.error('Export failed. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  if (!canView) return <AccessDenied />

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accounting Reports</h1>
          <p className="text-sm text-gray-500 mt-0.5">Tax reporting and financial overview for accounting and reconciliation</p>
        </div>
        {activeTab === 'tax' && (
          <button
            onClick={handleDownloadCSV}
            disabled={downloading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors whitespace-nowrap"
          >
            <ArrowDownTrayIcon className={`h-4 w-4 ${downloading ? 'animate-bounce' : ''}`} />
            {downloading ? 'Downloading…' : 'Export CSV'}
          </button>
        )}
      </div>

      {/* Date range + presets */}
      <DateRangeBar
        startDate={startDate}
        endDate={endDate}
        onStartChange={setStartDate}
        onEndChange={setEndDate}
      />

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {([
          { id: 'tax' as Tab, label: 'Tax Report' },
          ...(canFinancialTab
            ? [{ id: 'financial' as Tab, label: 'Financial Overview' }]
            : []),
        ]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'tax' && <TaxReportTab startDate={startDate} endDate={endDate} />}
      {activeTab === 'financial' && canFinancialTab && <FinancialOverviewTab startDate={startDate} endDate={endDate} />}
    </div>
  )
}

export default AccountingReportsPage
